import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  BarChart3, TrendingUp, Target, Award, AlertTriangle, CheckCircle,
  Globe, Lightbulb, Leaf, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, ReferenceLine, Cell, PieChart, Pie
} from 'recharts';

const API = BACKEND_URL;

const DIM_META = {
  social_footprint: { label: 'Social Footprint', short: 'Social', color: '#3B82F6', icon: Target },
  extroversion: { label: 'Extroversion', short: 'Extroversion', color: '#8B5CF6', icon: Globe },
  innovation: { label: 'Innovation', short: 'Innovation', color: '#F59E0B', icon: Lightbulb },
  sustainability: { label: 'Sustainability', short: 'Sustainability', color: '#10B981', icon: Leaf },
};

const STAGE_COLORS = { design: '#3B82F6', implementation: '#F59E0B', completion: '#10B981' };

const EvaluationBenchmark = ({ evaluations = [] }) => {
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareIds, setCompareIds] = useState([]);
  const [comparison, setComparison] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchBenchmarks(); }, []);

  const fetchBenchmarks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/investment-evaluation/benchmarks`, { headers });
      setBenchmarks(res.data);
    } catch {
      setBenchmarks(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompare = (evalId) => {
    setCompareIds(prev => {
      if (prev.includes(evalId)) return prev.filter(id => id !== evalId);
      if (prev.length >= 4) return prev;
      return [...prev, evalId];
    });
  };

  const runComparison = async () => {
    if (compareIds.length < 2) return;
    try {
      const res = await axios.get(`${API}/api/investment-evaluation/compare?ids=${compareIds.join(',')}`, { headers });
      setComparison(res.data.evaluations);
    } catch {
      setComparison(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  if (!benchmarks || benchmarks.evaluations_count === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-16">
          <BarChart3 className="h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-center">No evaluation data for benchmarking yet. Create and score evaluations first.</p>
        </CardContent>
      </Card>
    );
  }

  const dimAvgData = Object.entries(benchmarks.dimension_averages || {}).map(([key, val]) => ({
    dimension: DIM_META[key]?.short || key,
    fullLabel: DIM_META[key]?.label || key,
    average: val,
    fill: DIM_META[key]?.color || '#888',
  }));

  const stageAvgData = Object.entries(benchmarks.stage_averages || {}).map(([key, val]) => ({
    stage: key.charAt(0).toUpperCase() + key.slice(1),
    average: val,
    fill: STAGE_COLORS[key] || '#888',
  }));

  const scoreDistData = Object.entries(benchmarks.score_distribution || {}).map(([range, count]) => ({
    range, count,
  }));

  const sroiData = (benchmarks.sroi?.values || []).map(s => ({
    ...s,
    title: s.title.length > 25 ? s.title.substring(0, 25) + '...' : s.title,
  }));

  // Radar data for comparison
  const radarData = comparison ? Object.keys(DIM_META).map(dim => {
    const point = { dimension: DIM_META[dim].short };
    comparison.forEach((e, i) => {
      point[`eval_${i}`] = (e.dimension_scores || {})[dim] || 0;
    });
    return point;
  }) : [];

  const RADAR_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444'];

  return (
    <div className="space-y-6" data-testid="benchmark-section">
      <Tabs defaultValue="overview">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="bench-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="sroi" data-testid="bench-tab-sroi">SROI Analysis</TabsTrigger>
          <TabsTrigger value="compare" data-testid="bench-tab-compare">Compare</TabsTrigger>
        </TabsList>

        {/* ===== OVERVIEW TAB ===== */}
        <TabsContent value="overview">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{benchmarks.scored_count}</p>
                <p className="text-xs text-gray-500 mt-1">Scored Evaluations</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-amber-700">{benchmarks.overall_average}</p>
                <p className="text-xs text-gray-500 mt-1">Average Score / 5</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-700">{benchmarks.sroi?.average || '—'}</p>
                <p className="text-xs text-gray-500 mt-1">Avg SROI Ratio</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-violet-700">{benchmarks.sroi?.above_threshold || 0}/{benchmarks.sroi?.count || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Above SROI 1.5 Threshold</p>
              </CardContent>
            </Card>
          </div>

          {/* Dimension Averages Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Dimension Averages</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dimAvgData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} fontSize={11} />
                    <YAxis dataKey="dimension" type="category" width={90} fontSize={11} />
                    <Tooltip formatter={(val) => [val.toFixed(2), 'Avg Score']} />
                    <Bar dataKey="average" radius={[0, 4, 4, 0]} barSize={24}>
                      {dimAvgData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Stage Averages</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stageAvgData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" fontSize={11} />
                    <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} fontSize={11} />
                    <Tooltip formatter={(val) => [val.toFixed(2), 'Avg Score']} />
                    <Bar dataKey="average" radius={[4, 4, 0, 0]} barSize={40}>
                      {stageAvgData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Score Distribution */}
          <Card className="mb-6">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Score Distribution</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={scoreDistData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" fontSize={11} />
                  <YAxis allowDecimals={false} fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} barSize={36} name="Evaluations" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top & Bottom Performers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><ArrowUpRight className="h-4 w-4 text-green-500" /> Top Performers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(benchmarks.top_performers || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-green-50/50">
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-amber-700" />
                      <div>
                        <p className="text-xs font-medium text-gray-800">{p.title}</p>
                        <Badge variant="outline" className="text-xs capitalize">{p.stage}</Badge>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-green-700">{p.score}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><ArrowDownRight className="h-4 w-4 text-red-600" /> Needs Improvement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(benchmarks.bottom_performers || []).length > 0 ? (benchmarks.bottom_performers || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-red-50/50">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-400" />
                      <div>
                        <p className="text-xs font-medium text-gray-800">{p.title}</p>
                        <Badge variant="outline" className="text-xs capitalize">{p.stage}</Badge>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-red-600">{p.score}</span>
                  </div>
                )) : (
                  <p className="text-xs text-gray-500 text-center py-4">Not enough data</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== SROI TAB ===== */}
        <TabsContent value="sroi">
          <div className="space-y-4">
            {/* SROI Threshold Chart */}
            {sroiData.length > 0 ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                    <TrendingUp className="h-4 w-4 text-amber-700" /> SROI Ratios vs 1.5 Threshold
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={sroiData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="title" fontSize={10} angle={-15} textAnchor="end" height={60} />
                      <YAxis fontSize={11} />
                      <Tooltip
                        formatter={(val, name) => [typeof val === 'number' ? val.toFixed(2) : val, name === 'sroi_ratio' ? 'SROI Ratio' : name]}
                        labelFormatter={(label) => `Investment: ${label}`}
                      />
                      <ReferenceLine y={1.5} stroke="#EF4444" strokeDasharray="5 5" label={{ value: "Threshold 1.5", position: "right", fontSize: 10, fill: "#EF4444" }} />
                      <Bar dataKey="sroi_ratio" name="SROI Ratio" radius={[4, 4, 0, 0]} barSize={30}>
                        {sroiData.map((entry, i) => (
                          <Cell key={i} fill={entry.sroi_ratio >= 1.5 ? '#10B981' : '#EF4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed"><CardContent className="py-10 text-center text-gray-500 text-sm">No SROI calculations yet</CardContent></Card>
            )}

            {/* SROI Details Table */}
            {sroiData.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">SROI Details</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 text-gray-500 font-medium">Investment</th>
                          <th className="text-left py-2 px-2 text-gray-500 font-medium">Stage</th>
                          <th className="text-right py-2 px-2 text-gray-500 font-medium">Input Cost</th>
                          <th className="text-right py-2 px-2 text-gray-500 font-medium">Net Value</th>
                          <th className="text-right py-2 px-2 text-gray-500 font-medium">SROI</th>
                          <th className="text-center py-2 px-2 text-gray-500 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(benchmarks.sroi?.values || []).map((s, i) => (
                          <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-2 px-2 font-medium text-gray-800">{s.title}</td>
                            <td className="py-2 px-2 capitalize text-gray-600">{s.stage}</td>
                            <td className="py-2 px-2 text-right text-gray-600">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(s.inputs_cost)}</td>
                            <td className="py-2 px-2 text-right text-gray-600">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(s.net_value)}</td>
                            <td className="py-2 px-2 text-right font-bold" style={{ color: s.sroi_ratio >= 1.5 ? '#10B981' : '#EF4444' }}>{s.sroi_ratio}:1</td>
                            <td className="py-2 px-2 text-center">
                              {s.sroi_ratio >= 1.5 ? (
                                <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="h-3 w-3 mr-0.5" />Pass</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-xs"><AlertTriangle className="h-3 w-3 mr-0.5" />Below</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ===== COMPARE TAB ===== */}
        <TabsContent value="compare">
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold">Select Evaluations to Compare (2-4)</CardTitle>
                  <Button size="sm" disabled={compareIds.length < 2} onClick={runComparison} className="bg-indigo-600 hover:bg-indigo-700" data-testid="run-compare-btn">
                    Compare ({compareIds.length} selected)
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[240px] overflow-y-auto">
                  {evaluations.filter(e => e.weighted_total > 0).map(ev => (
                    <div
                      key={ev.id}
                      className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${compareIds.includes(ev.id) ? 'bg-indigo-50 border-indigo-300' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
                      onClick={() => toggleCompare(ev.id)}
                      data-testid={`compare-item-${ev.id}`}
                    >
                      <div>
                        <p className="text-xs font-medium text-gray-800 truncate">{ev.investment_title}</p>
                        <Badge variant="outline" className="text-xs capitalize">{ev.stage}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-700">{ev.weighted_total}</span>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${compareIds.includes(ev.id) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                          {compareIds.includes(ev.id) && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Comparison Radar */}
            {comparison && comparison.length >= 2 && (
              <>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Dimension Comparison (Radar)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={320}>
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="dimension" fontSize={11} />
                        <PolarRadiusAxis domain={[0, 5]} tickCount={6} fontSize={10} />
                        {comparison.map((e, i) => (
                          <Radar
                            key={i}
                            name={e.title.length > 20 ? e.title.substring(0, 20) + '...' : e.title}
                            dataKey={`eval_${i}`}
                            stroke={RADAR_COLORS[i]}
                            fill={RADAR_COLORS[i]}
                            fillOpacity={0.15}
                            strokeWidth={2}
                          />
                        ))}
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Tooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Comparison Summary Table */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Side-by-Side Comparison</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-2 text-gray-500 font-medium">Metric</th>
                            {comparison.map((e, i) => (
                              <th key={i} className="text-center py-2 px-2 font-medium" style={{ color: RADAR_COLORS[i] }}>
                                {e.title.length > 20 ? e.title.substring(0, 20) + '...' : e.title}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b bg-gray-50">
                            <td className="py-2 px-2 font-semibold text-gray-700">Weighted Total</td>
                            {comparison.map((e, i) => <td key={i} className="py-2 px-2 text-center font-bold text-lg">{e.weighted_total}</td>)}
                          </tr>
                          {Object.entries(DIM_META).map(([dim, meta]) => (
                            <tr key={dim} className="border-b">
                              <td className="py-2 px-2 text-gray-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }}></span>{meta.label}</td>
                              {comparison.map((e, i) => {
                                const val = (e.dimension_scores || {})[dim] || 0;
                                return <td key={i} className="py-2 px-2 text-center font-medium">{val || '—'}</td>;
                              })}
                            </tr>
                          ))}
                          <tr className="border-b bg-amber-50/50">
                            <td className="py-2 px-2 font-semibold text-amber-700">SROI Ratio</td>
                            {comparison.map((e, i) => (
                              <td key={i} className="py-2 px-2 text-center font-bold" style={{ color: e.sroi_ratio >= 1.5 ? '#10B981' : '#EF4444' }}>
                                {e.sroi_ratio > 0 ? `${e.sroi_ratio}:1` : '—'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-2 px-2 text-gray-600">Stage</td>
                            {comparison.map((e, i) => <td key={i} className="py-2 px-2 text-center capitalize">{e.stage}</td>)}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EvaluationBenchmark;
